import { Box, Button, Chip, Divider, Switch, Typography, useMediaQuery } from "@mui/material";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useReducedMotion } from "framer-motion";
import { AvatarGenerator } from "random-avatar-generator";
import { usePostHog } from "@posthog/react";
import { Toast } from "./Toast";
import {
  CardDTO,
  ChallengeAction,
  ChallengeStep,
  GameResult,
  isTeamId,
  opposingTeam,
  PlayerInfo,
  TeamId,
} from "../interface/GameTypes";
import { SocketEvent } from "../interface/SocketEvents";
import {
  BiddingStartData,
  BiddingUpdateData,
  CardPlayedData,
  ChallengeUpdateData,
  Connection,
  GameOverData,
  GameSnapshotData,
  MatchDealIntroStateData,
  MatchPlayerNetPayload,
  MatchStateData,
  PlayerDisconnectedData,
  PlayerReconnectedData,
  ReceiveReactionData,
  ReactionAnnounceData,
  ReactionRateLimitedData,
  TableGiphyPlayedData,
  TableGiphyRateLimitedData,
  RoundCompleteData,
  TrumpPickedData,
  TrumpPickRequiredData,
  TurnStartData,
} from "../sockets-client";
import { RoundResultModal } from "./RoundResultModal";
import { GameTableWithPlayers, AvatarClickInfo } from "./TableWithAvatar/TableWithAvatar";
import { BidEntry, BiddingPanel } from "./BiddingPanel";
import { ChallengeChainModal } from "./ChallengeChainModal";
import { TrumpPickPanel } from "./TrumpPickPanel";
import { TrumpIndicator } from "./TrumpIndicator";
import { RoundArea } from "./RoundArea";
import { HandShuffleOverlay } from "./HandShuffleOverlay";
import { TrailingShuffleDealIntro } from "./TrailingShuffleDealIntro";
import { PlayerHand } from "./PlayerHand";
import { GameOverScreen } from "./GameOverScreen";
import { ConfettiCelebration } from "./ConfettiCelebration";
import { ReactionMenu } from "./ReactionMenu";
import { ReactionOverlay, ReactionEvent } from "./ReactionOverlay";
import { DealIntroWaitingTauntPanel, pickDefaultTrailingTargetUuid } from "./DealIntroWaitingTauntPanel";
import { describeReactionForAnnounce } from "../reactionThrowAssets";
import { TrumpRevealModal } from "./TrumpRevealModal";
import { WaitingForPlayerModal } from "./WaitingForPlayerModal";
import { TeamShuffleFlash } from "./TeamShuffleFlash";
import { TableGiphyOverlay } from "./TableGiphyOverlay";
import { TableGiphyInlinePanel } from "./TableGiphyInlinePanel";

const avatarGenerator = new AvatarGenerator();
const SUIT_SYMBOLS: Record<string, string> = {
  Spades: "♠",
  Hearts: "♥",
  Diamonds: "♦",
  Clubs: "♣",
};

const fmtSignedNet = (n: number) => `${n >= 0 ? "+" : ""}${n}`;

type GamePhase = "BIDDING" | "CHALLENGE" | "TRUMP_PICK" | "PLAYING";

interface PlayedCard {
  playerIndex: number;
  card: CardDTO;
}

interface Props {
  players: PlayerInfo[];
  myPlayerInfo: PlayerInfo;
  myHand: CardDTO[];
  /** From App: increments each `cardsDealt` — drives first-deal shuffle timing on the table. */
  handDealVersion: number;
  numDecks: number;
  dealerIndex: number;
  socketConnection: Connection;
  roomID: string;
  hostUUID: string;
  pendingSnapshot?: GameSnapshotData | null;
  onSnapshotConsumed?: () => void;
  onReturnToLobby?: () => void;
  /** Full-screen team roster after lobby shuffle, shown once the table mounts. */
  tableTeamShuffleReveal?: PlayerInfo[] | null;
  onTableTeamShuffleDismiss?: () => void;
  /** Signed in with Google — enables forfeit while a game is in progress. */
  signedIn?: boolean;
  initialDealIntro?: MatchDealIntroStateData | null;
}

const SidePlayer = ({
  p,
  myUUID,
  teamColor,
  isAttaCaller,
  matchNet,
}: {
  p: PlayerInfo;
  myUUID: string;
  teamColor: string;
  isAttaCaller?: boolean;
  /** Match net (ranked stake) for this seat; shown beside the name in the team sidebar. */
  matchNet?: number;
}) => {
  const avatarUrl = useMemo(() => (p.isBot ? null : avatarGenerator.generateRandomAvatar(p.uuid)), [p.uuid, p.isBot]);
  return (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
    <Box sx={{
      width: 32, height: 32, borderRadius: "50%", overflow: "hidden", flexShrink: 0,
      border: p.uuid === myUUID ? `2px solid ${teamColor}` : `2px solid ${teamColor}44`,
      display: "flex", alignItems: "center", justifyContent: "center",
      backgroundColor: p.isBot ? "rgba(255,255,255,0.08)" : "transparent",
    }}>
      {p.isBot ? (
        <SmartToyIcon sx={{ fontSize: 18, color: "rgba(255,255,255,0.4)" }} />
      ) : (
        <img
          src={avatarUrl!}
          alt={p.name ?? ""}
          style={{ width: "100%", height: "100%" }}
        />
      )}
    </Box>
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0, flex: 1 }}>
      <Box sx={{
        color: p.isBot ? "rgba(255,255,255,0.45)" : p.uuid === myUUID ? "#fff" : "rgba(255,255,255,0.75)",
        fontSize: "0.9rem",
        fontWeight: p.uuid === myUUID ? "bold" : 500,
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        fontStyle: p.isBot ? "italic" : "normal",
        minWidth: 0,
      }}>
        {p.name ?? `Seat ${p.seatIndex}`}
      </Box>
      {typeof matchNet === "number" && (
        <Box
          component="span"
          sx={{
            fontSize: "0.78rem",
            fontWeight: 700,
            color: "rgba(255,255,255,0.88)",
            flexShrink: 0,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {fmtSignedNet(matchNet)}
        </Box>
      )}
      {isAttaCaller && (
        <Chip
          label="Atta"
          size="small"
          sx={{
            height: 20,
            fontSize: "0.62rem",
            fontWeight: "bold",
            flexShrink: 0,
            backgroundColor: "rgba(102,187,106,0.25)",
            color: "#81c784",
            border: "1px solid rgba(102,187,106,0.45)",
          }}
        />
      )}
    </Box>
  </Box>
  );
};

const TeamSidebar = ({
  team,
  players,
  myUUID,
  teamColor,
  marks,
  isCallerTeam,
  attaCallerName,
  callerSeatIndex,
  getPlayerMatchNet,
}: {
  team: TeamId;
  players: PlayerInfo[];
  myUUID: string;
  teamColor: string;
  marks: number;
  isCallerTeam?: boolean;
  /** When this team called Atta, the bidder’s display name (sidebar subtitle). */
  attaCallerName?: string | null;
  callerSeatIndex?: number | null;
  getPlayerMatchNet?: (uuid: string) => number;
}) => (
  <Box sx={{
    ...(isCallerTeam !== undefined && {
      borderLeft: isCallerTeam ? "3px solid #66bb6a" : "3px solid #ef5350",
      pl: 1,
    }),
  }}>
    <Box sx={{
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr) auto",
      alignItems: "start",
      columnGap: 1.25,
      rowGap: 0.25,
      mb: 0.75,
    }}>
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.75 }}>
          <Box sx={{ color: teamColor, fontWeight: "bold", fontSize: "0.95rem", letterSpacing: 1 }}>
            TEAM {team}
          </Box>
          {isCallerTeam !== undefined && (
            <Box sx={{
              fontSize: "0.7rem",
              fontWeight: "bold",
              color: isCallerTeam ? "#66bb6a" : "#ef5350",
              backgroundColor: isCallerTeam ? "rgba(102,187,106,0.15)" : "rgba(239,83,80,0.15)",
              border: `1px solid ${isCallerTeam ? "rgba(102,187,106,0.4)" : "rgba(239,83,80,0.4)"}`,
              borderRadius: 1,
              px: 0.5,
              py: 0.1,
              lineHeight: 1.3,
              flexShrink: 0,
            }}>
              {isCallerTeam ? "CALLER" : "OPP"}
            </Box>
          )}
        </Box>
        {attaCallerName && isCallerTeam === true && (
          <Typography variant="caption" sx={{ display: "block", mt: 0.35, color: "rgba(255,255,255,0.78)", fontSize: "0.72rem", lineHeight: 1.3 }}>
            Atta:{" "}
            <Box component="span" sx={{ fontWeight: 700, color: "#81c784" }}>
              {attaCallerName}
            </Box>
          </Typography>
        )}
      </Box>
      <Box sx={{
        color: teamColor,
        fontWeight: "bold",
        fontSize: "1.5rem",
        lineHeight: 1,
        textAlign: "right",
        pt: 0.125,
        flexShrink: 0,
      }}>
        {marks}
      </Box>
    </Box>
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.6 }}>
      {players.map((p) => (
        <SidePlayer
          key={p.uuid}
          p={p}
          myUUID={myUUID}
          teamColor={teamColor}
          isAttaCaller={callerSeatIndex != null && p.seatIndex === callerSeatIndex}
          matchNet={getPlayerMatchNet?.(p.uuid)}
        />
      ))}
    </Box>
  </Box>
);

const ROUND_SHOW_MS = 200; // brief pause so last card is visible before modal appears

export const GameTable = ({
  players,
  myPlayerInfo,
  myHand: initialHand,
  handDealVersion,
  dealerIndex: initialDealerIndex,
  numDecks,
  socketConnection,
  roomID,
  hostUUID,
  pendingSnapshot,
  onSnapshotConsumed,
  onReturnToLobby,
  tableTeamShuffleReveal = null,
  onTableTeamShuffleDismiss,
  signedIn = false,
  initialDealIntro = null,
}: Props) => {
  const posthog = usePostHog();
  const [phase, setPhase] = useState<GamePhase>("BIDDING");
  const [dealerIndex, setDealerIndex] = useState(initialDealerIndex);
  /** Must match server `Addata.firstSeatRightOfDealer` (counter-clockwise from dealer), not `(dealer+1)%n`. */
  const [currentBidderIndex, setCurrentBidderIndex] = useState(() => {
    const n = players.length;
    if (n <= 0) return 0;
    return (initialDealerIndex - 1 + n) % n;
  });
  const [bidHistory, setBidHistory] = useState<BidEntry[]>([]);
  const [currentCallerIndex, setCurrentCallerIndex] = useState<number | null>(null);
  const [currentTurnIndex, setCurrentTurnIndex] = useState<number | null>(null);
  const [roundLeaderIndex, setRoundLeaderIndex] = useState<number | null>(null);
  const [trumpStake, setTrumpStake] = useState(0);
  const [stakePulseTick, setStakePulseTick] = useState(0);
  const [challengeBroadcast, setChallengeBroadcast] = useState<string | null>(null);
  const [challengeLevel, setChallengeLevel] = useState<"Atta" | "Addu" | "Sharathu" | "BeSharathu">("Atta");
  const [challengeStep, setChallengeStep] = useState<ChallengeStep | null>(null);
  const [challengeRespondingTeam, setChallengeRespondingTeam] = useState<TeamId | null>(null);
  const [challengePassedSeats, setChallengePassedSeats] = useState<number[]>([]);
  const [trumpSuit, setTrumpSuit] = useState<string | null>(null);
  const [trumpCallerTeam, setTrumpCallerTeam] = useState<TeamId | null>(null);
  const [currentRound, setCurrentRound] = useState<PlayedCard[]>([]);
  const [ledSuit, setLedSuit] = useState<string | null>(null);
  const [hand, setHand] = useState<CardDTO[]>(initialHand);
  const [teamAMarks, setTeamAMarks] = useState(0);
  const [teamBMarks, setTeamBMarks] = useState(0);
  const [teamARoundWins, setTeamARoundWins] = useState(0);
  const [teamBRoundWins, setTeamBRoundWins] = useState(0);
  const [totalRounds, setTotalRounds] = useState(numDecks * 6);
  const [gameResult, setGameResult] = useState<GameResult | null>(null);
  const [gameOverExtras, setGameOverExtras] = useState<{
    matchTeamPoints?: { A: number; B: number };
    matchGamesPlayed?: number;
    matchPlayerPoints?: MatchPlayerNetPayload[];
  } | null>(null);
  const [matchHud, setMatchHud] = useState<{ A: number; B: number; dealerSeat: number; gamesPlayed: number }>({
    A: 0,
    B: 0,
    dealerSeat: 0,
    gamesPlayed: 0,
  });
  const [matchPlayerProgress, setMatchPlayerProgress] = useState<MatchPlayerNetPayload[]>([]);
  const [dealIntro, setDealIntro] = useState<MatchDealIntroStateData | null>(null);
  const [redealMessage, setRedealMessage] = useState<string | null>(null);
  const [reactionAnnounce, setReactionAnnounce] = useState<string | null>(null);
  const [showMyTurnToast, setShowMyTurnToast] = useState(false);
  const [myTurnToastKey, setMyTurnToastKey] = useState(0);
  const [forcedBid, setForcedBid] = useState(false);
  const [disconnectedPlayerName, setDisconnectedPlayerName] = useState<string | null>(null);
  const [disconnectedSeatIndex, setDisconnectedSeatIndex] = useState<number | null>(null);
  const [slamSeatIndex, setSlamSeatIndex] = useState<number | null>(null);
  const [slamMotionTick, setSlamMotionTick] = useState(0);
  const [trumpRippleTick, setTrumpRippleTick] = useState(0);
  const [roundResult, setRoundResult] = useState<RoundCompleteData | null>(null);
  const [showTrumpReveal, setShowTrumpReveal] = useState(false);
  const [roundExpanded, setRoundExpanded] = useState(true);
  const [showCardPoints, setShowCardPoints] = useState(true);
  const isShortLandscapePhoneLike = useMediaQuery("(max-width:1024px) and (max-height:520px)");

  /** First deal only: shuffle on green felt, then reveal hand. */
  const reduceMotionPref = useReducedMotion() ?? false;
  const [deal1Phase, setDeal1Phase] = useState<"na" | "shuffle" | "shown">("na");

  useEffect(() => {
    if (handDealVersion === 0) {
      setDeal1Phase("na");
      return;
    }
    if (handDealVersion >= 2) {
      setDeal1Phase("shown");
      return;
    }
    if (handDealVersion === 1 && reduceMotionPref) {
      setDeal1Phase("shown");
      return;
    }
    // Match trailing shuffle intro replaces the shared felt shuffle; avoid one long chained animation for everyone.
    if (handDealVersion === 1 && dealIntro) {
      setDeal1Phase("shown");
      return;
    }
    if (handDealVersion === 1) {
      setDeal1Phase("shuffle");
      const t = window.setTimeout(() => setDeal1Phase("shown"), 2600);
      return () => clearTimeout(t);
    }
  }, [handDealVersion, reduceMotionPref, dealIntro]);

  const roundTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const deferredTurnStartRef = useRef<TurnStartData | null>(null);

  // ── Reactions state ──
  const [reactionQueue, setReactionQueue] = useState<ReactionEvent[]>([]);
  const [reactionMenuTarget, setReactionMenuTarget] = useState<AvatarClickInfo | null>(null);
  const [reactionCooldownMs, setReactionCooldownMs] = useState(0);
  const cooldownTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seenReactionIdsRef = useRef(new Set<string>());

  const [giphyQueue, setGiphyQueue] = useState<TableGiphyPlayedData[]>([]);
  const [giphyCooldownMs, setGiphyCooldownMs] = useState(0);
  const seenGiphyIdsRef = useRef(new Set<string>());
  const giphyCooldownTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const dismissGiphyHead = useCallback(() => {
    setGiphyQueue((q) => q.slice(1));
  }, []);

  const phaseRef = useRef<GamePhase>("BIDDING");
  const trumpSuitRef = useRef<string | null>(null);
  const stakeRef = useRef(0);
  const wasMyTurnPromptActiveRef = useRef(false);

  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);
  useEffect(() => {
    trumpSuitRef.current = trumpSuit;
  }, [trumpSuit]);
  useEffect(() => {
    stakeRef.current = trumpStake;
  }, [trumpStake]);

  useEffect(() => { setHand(initialHand); }, [initialHand]);
  useEffect(() => {
    if (initialDealIntro) setDealIntro(initialDealIntro);
  }, [initialDealIntro]);

  const playerBySeat = useCallback(
    (seatIndex: number | null | undefined): PlayerInfo | undefined => {
      if (typeof seatIndex !== "number") return undefined;
      return players.find((p) => p.seatIndex === seatIndex);
    },
    [players]
  );

  const applyTurnStart = (data: TurnStartData) => {
    phaseRef.current = "PLAYING";
    setCurrentTurnIndex(data.playerIndex);
    setRoundLeaderIndex(data.playerIndex);
    setCurrentRound([]);
    setLedSuit(null);
    setPhase("PLAYING");
  };

  const flushDeferredTurnStart = () => {
    if (deferredTurnStartRef.current) {
      applyTurnStart(deferredTurnStartRef.current);
      deferredTurnStartRef.current = null;
    }
  };

  const handleRoundResultClose = () => {
    setRoundResult(null);
    flushDeferredTurnStart();
  };

  useEffect(() => {
    if (!pendingSnapshot) return;
    phaseRef.current = pendingSnapshot.phase;
    setPhase(pendingSnapshot.phase);
    setDealerIndex(pendingSnapshot.dealerIndex);
    setCurrentBidderIndex(pendingSnapshot.currentBidderIndex);
    setCurrentCallerIndex(pendingSnapshot.callerIndex);
    trumpSuitRef.current = pendingSnapshot.trumpSuit;
    setTrumpSuit(pendingSnapshot.trumpSuit);
    setTrumpCallerTeam(pendingSnapshot.callerTeam);
    setTrumpStake(pendingSnapshot.stake);
    setCurrentTurnIndex(pendingSnapshot.currentTurnIndex);
    setRoundLeaderIndex(pendingSnapshot.currentTurnIndex);
    setTeamAMarks(pendingSnapshot.teamAMarks);
    setTeamBMarks(pendingSnapshot.teamBMarks);
    setTeamARoundWins(pendingSnapshot.teamARoundWins ?? 0);
    setTeamBRoundWins(pendingSnapshot.teamBRoundWins ?? 0);
    setCurrentRound(pendingSnapshot.currentRound);
    setLedSuit(pendingSnapshot.ledSuit);
    if (pendingSnapshot.matchTeamPoints) {
      setMatchHud((h) => ({
        ...h,
        A: pendingSnapshot.matchTeamPoints!.A,
        B: pendingSnapshot.matchTeamPoints!.B,
        dealerSeat:
          typeof pendingSnapshot.matchDealerSeat === "number" ? pendingSnapshot.matchDealerSeat! : h.dealerSeat,
        gamesPlayed:
          typeof pendingSnapshot.matchGamesPlayed === "number" ? pendingSnapshot.matchGamesPlayed! : h.gamesPlayed,
      }));
    }
    if (pendingSnapshot.matchPlayerPoints) {
      setMatchPlayerProgress(pendingSnapshot.matchPlayerPoints);
    }
    if (pendingSnapshot.matchDealIntroActive && pendingSnapshot.matchTrailingTeam != null) {
      setDealIntro({
        matchTeamPoints: pendingSnapshot.matchTeamPoints ?? { A: 0, B: 0 },
        trailingTeam: pendingSnapshot.matchTrailingTeam,
        tapsByUuid: pendingSnapshot.trailingShuffleTapsByUuid ?? {},
        deadlineMs: pendingSnapshot.matchDealIntroDeadlineMs ?? null,
      });
    } else {
      setDealIntro(null);
    }
    if (pendingSnapshot.phase === "CHALLENGE") {
      setChallengeStep(pendingSnapshot.challengeStep ?? null);
      setChallengeRespondingTeam(pendingSnapshot.respondingTeam ?? null);
      setChallengePassedSeats(pendingSnapshot.challengePasses ?? []);
    } else {
      setChallengeStep(null);
      setChallengeRespondingTeam(null);
      setChallengePassedSeats([]);
    }
    onSnapshotConsumed?.();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSnapshot]);

  useEffect(() => {
    socketConnection.listenForBiddingStart((data: BiddingStartData) => {
      if (roundTimerRef.current) { clearTimeout(roundTimerRef.current); roundTimerRef.current = null; }
      deferredTurnStartRef.current = null;

      setCurrentBidderIndex(data.currentBidderIndex);
      setDealerIndex(data.dealerIndex);
      setBidHistory([]);
      setCurrentCallerIndex(null);
      setCurrentTurnIndex(null);
      setRoundLeaderIndex(null);
      trumpSuitRef.current = null;
      setTrumpSuit(null);
      setTrumpCallerTeam(null);
      setCurrentRound([]);
      setLedSuit(null);
      setTeamAMarks(0);
      setTeamBMarks(0);
      setTeamARoundWins(0);
      setTeamBRoundWins(0);
      setGameResult(null);
      setGameOverExtras(null);
      setDealIntro(null);
      setRoundResult(null);
      setForcedBid(data.forcedBid ?? false);
      setChallengeStep(null);
      setChallengeRespondingTeam(null);
      setChallengePassedSeats([]);
      phaseRef.current = "BIDDING";
      setPhase("BIDDING");
      if (data.redeal) {
        setRedealMessage("Everyone passed — reshuffling and re-dealing cards…");
      }
    });

    socketConnection.listenForBiddingUpdate((data: BiddingUpdateData) => {
      const bidder = playerBySeat(data.bidderIndex);
      const name = bidder?.name ?? `Seat ${data.bidderIndex}`;
      setBidHistory((prev) => [
        ...prev,
        { seatIndex: data.bidderIndex, playerName: name, action: data.action },
      ]);
      setCurrentBidderIndex(data.nextBidderIndex);
      setCurrentCallerIndex(data.currentCallerIndex);
      setForcedBid(data.forcedBid ?? false);
      if (data.biddingComplete) {
        if (data.callerStake !== undefined) setTrumpStake(data.callerStake);
        setChallengeLevel("Atta");
        setChallengeStep(null);
        setChallengeRespondingTeam(null);
        setChallengePassedSeats([]);
        phaseRef.current = "TRUMP_PICK";
        setPhase("TRUMP_PICK");
      }
    });

    socketConnection.listenForChallengeUpdate((data: ChallengeUpdateData) => {
      const prevStake = stakeRef.current;
      setTrumpStake(data.stake);
      if (data.stake > prevStake) {
        setStakePulseTick((t) => t + 1);
      }
      const actorName = playerBySeat(data.seatIndex)?.name ?? data.playerName ?? `Seat ${data.seatIndex}`;
      if (data.action === ChallengeAction.Pass) {
        setChallengeBroadcast(`${actorName} passed`);
      } else {
        if (data.action === ChallengeAction.Addu) setChallengeLevel("Addu");
        if (data.action === ChallengeAction.Sharathu) setChallengeLevel("Sharathu");
        if (data.action === ChallengeAction.BeSharathu) setChallengeLevel("BeSharathu");
        setChallengeBroadcast(`${actorName} called ${data.action} — stake raised to ${data.stake}!`);
      }
      if (data.trumpPickNext) {
        setChallengeStep(null);
        setChallengeRespondingTeam(null);
        setChallengePassedSeats([]);
        /* Server `finishChallengeToPlaying()` is already PLAYING; never go back to TRUMP_PICK here —
           that desynced the strip ("…picking trump") and could stack with a stale TrumpPickRequired. */
        setShowTrumpReveal(false);
        phaseRef.current = "PLAYING";
        setPhase("PLAYING");
        setTimeout(() => setChallengeBroadcast(null), 2500);
        return;
      }
      if (data.challengeStep && isTeamId(data.respondingTeam)) {
        setChallengeStep(data.challengeStep);
        setChallengeRespondingTeam(data.respondingTeam);
        setChallengePassedSeats(data.passedSeatIndices ?? []);
        phaseRef.current = "CHALLENGE";
        setPhase("CHALLENGE");
      }
      setTimeout(() => setChallengeBroadcast(null), 2500);
    });

    socketConnection.listenForTrumpPickRequired((data: TrumpPickRequiredData) => {
      if (trumpSuitRef.current != null) {
        /* Duplicate / late delivery after caller already chose — do not reopen trump-pick UI. */
        return;
      }
      setCurrentCallerIndex(data.callerIndex);
      setTrumpStake(data.stake);
      setChallengeStep(null);
      setChallengeRespondingTeam(null);
      setChallengePassedSeats([]);
      phaseRef.current = "TRUMP_PICK";
      setPhase("TRUMP_PICK");
    });

    socketConnection.listenForTrumpPicked((data: TrumpPickedData) => {
      const hadTrump = trumpSuitRef.current != null;
      trumpSuitRef.current = data.suit;
      setTrumpSuit(data.suit);
      setTrumpCallerTeam(data.callerTeam);
      setTrumpStake(data.stake);
      setChallengeLevel("Atta");
      setCurrentCallerIndex(data.callerIndex);
      setTotalRounds(numDecks * (players.length <= 4 ? 6 : players.length <= 6 ? 8 : 6));
      if (!hadTrump) {
        setShowTrumpReveal(true);
        phaseRef.current = "CHALLENGE";
        setPhase("CHALLENGE");
        setChallengeStep(ChallengeStep.Addu);
        setChallengePassedSeats([]);
        if (isTeamId(data.callerTeam)) {
          setChallengeRespondingTeam(opposingTeam(data.callerTeam));
        }
      } else {
        phaseRef.current = "PLAYING";
        setPhase("PLAYING");
        queueMicrotask(() => {
          socketConnection.acknowledgeTrump();
        });
      }
    });

    socketConnection.listenForTurnStart((data: TurnStartData) => {
      if (roundTimerRef.current !== null) {
        deferredTurnStartRef.current = data;
      } else {
        applyTurnStart(data);
      }
    });

    socketConnection.listenForCardPlayed((data: CardPlayedData) => {
      if (deferredTurnStartRef.current) {
        if (roundTimerRef.current) { clearTimeout(roundTimerRef.current); roundTimerRef.current = null; }
        setRoundResult(null);
        flushDeferredTurnStart();
      }
      setCurrentRound(data.round);
      if (data.round.length === 1) {
        setLedSuit(data.round[0].card.suit);
      }
      if (data.nextPlayerIndex !== null && data.nextPlayerIndex !== undefined) {
        setCurrentTurnIndex(data.nextPlayerIndex);
      }
      if (phaseRef.current === "PLAYING") {
        setSlamSeatIndex(data.playerIndex);
        setSlamMotionTick((t) => t + 1);
        const trump = trumpSuitRef.current;
        if (trump && data.card.suit === trump) {
          setTrumpRippleTick((t) => t + 1);
        }
      }
      if (data.playerIndex === myPlayerInfo.seatIndex) {
        setHand((prev) => {
          const idx = prev.findIndex(
            (c) => c.suit === data.card.suit && c.rank === data.card.rank
          );
          if (idx === -1) return prev;
          return [...prev.slice(0, idx), ...prev.slice(idx + 1)];
        });
      }
    });

    socketConnection.listenForRoundComplete((data: RoundCompleteData) => {
      setTeamAMarks(data.teamAMarks);
      setTeamBMarks(data.teamBMarks);
      setTeamARoundWins(data.teamARoundWins);
      setTeamBRoundWins(data.teamBRoundWins);
      roundTimerRef.current = setTimeout(() => {
        roundTimerRef.current = null;
        setRoundResult(data);
      }, ROUND_SHOW_MS);
    });

    socketConnection.listenForGameOver((data: GameOverData) => {
      if (roundTimerRef.current) { clearTimeout(roundTimerRef.current); roundTimerRef.current = null; }
      deferredTurnStartRef.current = null;
      setRoundResult(null);
      setGameResult(data.result);
      setGameOverExtras({
        matchTeamPoints: data.matchTeamPoints,
        matchGamesPlayed: data.matchGamesPlayed,
        matchPlayerPoints: data.matchPlayerPoints,
      });
      if (data.matchTeamPoints) {
        setMatchHud((h) => ({
          ...h,
          A: data.matchTeamPoints!.A,
          B: data.matchTeamPoints!.B,
          dealerSeat: typeof data.matchDealerSeat === "number" ? data.matchDealerSeat! : h.dealerSeat,
          gamesPlayed: typeof data.matchGamesPlayed === "number" ? data.matchGamesPlayed! : h.gamesPlayed,
        }));
      }
      if (data.matchPlayerPoints) {
        setMatchPlayerProgress(data.matchPlayerPoints);
      }
    });

    socketConnection.listenForMatchState((data: MatchStateData) => {
      setMatchHud({
        A: data.matchTeamPoints.A,
        B: data.matchTeamPoints.B,
        dealerSeat: data.matchDealerSeat,
        gamesPlayed: data.matchGamesPlayed,
      });
      if (data.matchPlayerPoints) {
        setMatchPlayerProgress(data.matchPlayerPoints);
      }
    });

    socketConnection.listenForMatchDealIntroState((data: MatchDealIntroStateData) => {
      setDealIntro(data);
      setMatchHud((h) => ({
        ...h,
        A: data.matchTeamPoints.A,
        B: data.matchTeamPoints.B,
      }));
    });

    socketConnection.listenForPlayerDisconnected((data: PlayerDisconnectedData) => {
      setDisconnectedPlayerName(data.playerName ?? `Seat ${data.seatIndex}`);
      setDisconnectedSeatIndex(data.seatIndex);
    });

    socketConnection.listenForPlayerReconnected((_data: PlayerReconnectedData) => {
      setDisconnectedPlayerName(null);
      setDisconnectedSeatIndex(null);
    });

    socketConnection.listenForReceiveReaction((data: ReceiveReactionData) => {
      if (seenReactionIdsRef.current.has(data.eventId)) return;
      seenReactionIdsRef.current.add(data.eventId);
      setReactionQueue((prev) => [
        ...prev,
        { eventId: data.eventId, fromPlayerName: data.fromPlayerName, reactionType: data.reactionType },
      ]);
    });

    socketConnection.listenForReactionAnnounce((data: ReactionAnnounceData) => {
      setReactionAnnounce(
        `${data.fromPlayerName} sent ${describeReactionForAnnounce(data.reactionType)} to ${data.toPlayerName}`
      );
    });

    socketConnection.listenForReactionRateLimited((data: ReactionRateLimitedData) => {
      setReactionCooldownMs(data.retryAfterMs);
      if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current);
      cooldownTimerRef.current = setTimeout(() => setReactionCooldownMs(0), data.retryAfterMs);
    });

    socketConnection.listenForTableGiphyPlayed((data: TableGiphyPlayedData) => {
      if (seenGiphyIdsRef.current.has(data.eventId)) return;
      seenGiphyIdsRef.current.add(data.eventId);
      setGiphyQueue((q) => [...q, data]);
    });

    socketConnection.listenForTableGiphyRateLimited((data: TableGiphyRateLimitedData) => {
      setGiphyCooldownMs(data.retryAfterMs);
      if (giphyCooldownTimerRef.current) clearTimeout(giphyCooldownTimerRef.current);
      giphyCooldownTimerRef.current = setTimeout(() => setGiphyCooldownMs(0), data.retryAfterMs);
    });

    return () => {
      if (roundTimerRef.current) clearTimeout(roundTimerRef.current);
      if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current);
      if (giphyCooldownTimerRef.current) clearTimeout(giphyCooldownTimerRef.current);
      socketConnection.removeListener(SocketEvent.BiddingStart);
      socketConnection.removeListener(SocketEvent.BiddingUpdate);
      socketConnection.removeListener(SocketEvent.ChallengeUpdate);
      socketConnection.removeListener(SocketEvent.TrumpPickRequired);
      socketConnection.removeListener(SocketEvent.TrumpPicked);
      socketConnection.removeListener(SocketEvent.TurnStart);
      socketConnection.removeListener(SocketEvent.CardPlayed);
      socketConnection.removeListener(SocketEvent.RoundComplete);
      socketConnection.removeListener(SocketEvent.GameOver);
      socketConnection.removeListener(SocketEvent.MatchState);
      socketConnection.removeListener(SocketEvent.MatchDealIntroState);
      socketConnection.removeListener(SocketEvent.PlayerDisconnected);
      socketConnection.removeListener(SocketEvent.PlayerReconnected);
      socketConnection.removeListener(SocketEvent.ReceiveReaction);
      socketConnection.removeListener(SocketEvent.ReactionAnnounce);
      socketConnection.removeListener(SocketEvent.ReactionRateLimited);
      socketConnection.removeListener(SocketEvent.TableGiphyPlayed);
      socketConnection.removeListener(SocketEvent.TableGiphyRateLimited);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socketConnection, players, myPlayerInfo.seatIndex, numDecks]);

  const handleBid = (action: "Atta" | "Pass") => {
    posthog?.capture("bid_placed", { action, room_id: roomID });
    socketConnection.placeBid(action);
  };
  const handlePickTrump = (suit: string) => {
    posthog?.capture("trump_picked", { suit, room_id: roomID });
    socketConnection.pickTrump(suit);
  };
  const handleChallengeChoice = (action: ChallengeAction) => {
    posthog?.capture("challenge_response", { action, room_id: roomID, step: challengeStep });
    socketConnection.challengeResponse(action);
  };
  const handlePlayCard = (card: CardDTO) => {
    posthog?.capture("card_played", { suit: card.suit, rank: card.rank, room_id: roomID });
    socketConnection.playCard(card.suit, card.rank);
  };
  const handlePlayAgain = () => socketConnection.playAgain(false);

  const handleTrumpRevealDismiss = useCallback(() => {
    setShowTrumpReveal(false);
    socketConnection.acknowledgeTrump();
  }, [socketConnection]);

  const handleAvatarClick = useCallback((info: AvatarClickInfo) => {
    setReactionMenuTarget(info);
  }, []);

  const handleReactionSelect = useCallback((type: string) => {
    if (!reactionMenuTarget) return;
    const clientEventId = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    socketConnection.sendReaction(reactionMenuTarget.playerUUID, type, clientEventId);
    setReactionMenuTarget(null);
  }, [reactionMenuTarget, socketConnection]);

  const closeReactionMenu = useCallback(() => setReactionMenuTarget(null), []);

  const sendDealIntroTauntReaction = useCallback(
    (reactionId: string) => {
      if (!dealIntro) return;
      const uuid = pickDefaultTrailingTargetUuid(players, dealIntro.trailingTeam);
      if (!uuid) return;
      const clientEventId = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      socketConnection.sendReaction(uuid, reactionId, clientEventId);
    },
    [dealIntro, players, socketConnection]
  );

  const handleReactionComplete = useCallback((eventId: string) => {
    setReactionQueue((prev) => prev.filter((r) => r.eventId !== eventId));
  }, []);

  const shareTableGiphyPick = useCallback(
    (giphyId: string) => {
      const clientEventId = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      socketConnection.shareTableGiphy(giphyId, clientEventId);
    },
    [socketConnection]
  );

  const callerName =
    currentCallerIndex !== null
      ? (playerBySeat(currentCallerIndex)?.name ?? `Seat ${currentCallerIndex}`)
      : "";


  const turnLabel = (() => {
    if (phase === "PLAYING") {
      if (currentTurnIndex !== null) {
        return currentTurnIndex === myPlayerInfo.seatIndex
          ? "YOUR TURN — play a card!"
          : `${playerBySeat(currentTurnIndex)?.name ?? `Seat ${currentTurnIndex}`}'s turn`;
      }
      if (trumpSuit) {
        return "Finishing deal — hang tight…";
      }
      return null;
    }
    if (phase === "BIDDING") {
      const bidderName = playerBySeat(currentBidderIndex)?.name ?? `Seat ${currentBidderIndex}`;
      return currentBidderIndex === myPlayerInfo.seatIndex
        ? "YOUR BID"
        : `${bidderName} is bidding`;
    }
    if (phase === "TRUMP_PICK") {
      if (trumpSuit) {
        return "Dealing remaining cards…";
      }
      return currentCallerIndex === myPlayerInfo.seatIndex
        ? "PICK TRUMP"
        : `${callerName} is picking trump`;
    }
    if (phase === "CHALLENGE" && challengeStep && challengeRespondingTeam) {
      const teamWait = challengeRespondingTeam === TeamId.A ? "Team A" : "Team B";
      const stepLabel =
        challengeStep === ChallengeStep.Addu
          ? "Addu"
          : challengeStep === ChallengeStep.Sharathu
            ? "Sharathu"
            : "Be Sharathu";
      const suitLabel = trumpSuit ? `${SUIT_SYMBOLS[trumpSuit] ?? trumpSuit} ${trumpSuit}` : null;
      if (myPlayerInfo.teamId === challengeRespondingTeam) {
        return suitLabel
          ? `YOUR TEAM — ${stepLabel} (${suitLabel}) or Pass`
          : `YOUR TEAM — ${stepLabel} or Pass`;
      }
      return suitLabel
        ? `Challenge: waiting for ${teamWait} (${stepLabel} on ${suitLabel})`
        : `Challenge: waiting for ${teamWait} (${stepLabel})`;
    }
    return null;
  })();

  const isMyActiveLabel =
    (phase === "PLAYING" && currentTurnIndex === myPlayerInfo.seatIndex) ||
    (phase === "BIDDING" && currentBidderIndex === myPlayerInfo.seatIndex) ||
    (phase === "TRUMP_PICK" &&
      !trumpSuit &&
      currentCallerIndex === myPlayerInfo.seatIndex) ||
    (phase === "CHALLENGE" &&
      challengeRespondingTeam !== null &&
      myPlayerInfo.teamId === challengeRespondingTeam);
  const myPlayedThisRound = currentRound.some((pc) => pc.playerIndex === myPlayerInfo.seatIndex);

  useEffect(() => {
    const active =
      phase === "PLAYING" &&
      currentTurnIndex === myPlayerInfo.seatIndex &&
      !myPlayedThisRound &&
      !roundResult;
    if (active && !wasMyTurnPromptActiveRef.current) {
      setShowMyTurnToast(true);
      setMyTurnToastKey((k) => k + 1);
    }
    if (!active) {
      setShowMyTurnToast(false);
    }
    wasMyTurnPromptActiveRef.current = active;
  }, [phase, currentTurnIndex, myPlayerInfo.seatIndex, myPlayedThisRound, roundResult]);

  const teamAPlayers = players.filter((p) => p.teamId === TeamId.A);
  const teamBPlayers = players.filter((p) => p.teamId === TeamId.B);
  const callerTeamId = currentCallerIndex !== null ? playerBySeat(currentCallerIndex)?.teamId : undefined;
  const isTableHost = myPlayerInfo.uuid === hostUUID;
  const showStakeHud =
    phase === "BIDDING" ||
    (trumpStake > 0 && (phase === "CHALLENGE" || phase === "PLAYING" || phase === "TRUMP_PICK"));
  const currentStakeLabel =
    challengeLevel === "BeSharathu" ? "Besharathu" : challengeLevel;

  const showBidStrip = phase === "BIDDING" || phase === "CHALLENGE";
  const challengeModalOpen =
    phase === "CHALLENGE" &&
    challengeStep !== null &&
    challengeRespondingTeam !== null &&
    myPlayerInfo.teamId === challengeRespondingTeam;

  const individualRows: MatchPlayerNetPayload[] =
    matchPlayerProgress.length > 0
      ? matchPlayerProgress
      : players.map((p) => ({
          uuid: p.uuid,
          name: p.name,
          seatIndex: p.seatIndex,
          teamId: p.teamId,
          net: 0,
        }));

  return (
    <>
      {tableTeamShuffleReveal &&
        tableTeamShuffleReveal.length > 0 &&
        onTableTeamShuffleDismiss && (
          <TeamShuffleFlash
            players={tableTeamShuffleReveal}
            seatingMode="teams"
            onDismiss={onTableTeamShuffleDismiss}
          />
        )}
      <WaitingForPlayerModal
        open={disconnectedPlayerName !== null}
        playerName={disconnectedPlayerName}
        onReturnToLobby={onReturnToLobby ?? (() => {})}
        isHost={isTableHost}
        onFillWithBot={isTableHost ? () => socketConnection.addBotDisconnected() : undefined}
        onRemovePlayer={
          isTableHost && disconnectedSeatIndex !== null
            ? () => {
                const u = playerBySeat(disconnectedSeatIndex)?.uuid;
                if (!u) return;
                socketConnection.hostKickPlayer(u);
                setDisconnectedPlayerName(null);
                setDisconnectedSeatIndex(null);
              }
            : undefined
        }
      />
      {challengeStep && challengeRespondingTeam && (
        <ChallengeChainModal
          open={challengeModalOpen}
          step={challengeStep}
          stake={trumpStake}
          trumpSuit={trumpSuit}
          myHand={hand}
          callerLabel={callerName}
          onChoose={handleChallengeChoice}
          mySeatIndex={myPlayerInfo.seatIndex}
          passedSeatIndices={challengePassedSeats}
          players={players}
          respondingTeam={challengeRespondingTeam}
        />
      )}
      {showTrumpReveal && trumpSuit && trumpCallerTeam && (
        <TrumpRevealModal
          suit={trumpSuit}
          callerTeam={trumpCallerTeam}
          callerName={callerName}
          onDismiss={handleTrumpRevealDismiss}
        />
      )}
      {roundResult && (
        <RoundResultModal
          winnerName={playerBySeat(roundResult.winnerIndex)?.name ?? `Seat ${roundResult.winnerIndex}`}
          winnerTeam={roundResult.winnerTeam}
          winnerIndex={roundResult.winnerIndex}
          round={roundResult.round}
          players={players}
          trumpSuit={trumpSuit}
          teamARoundWins={roundResult.teamARoundWins}
          teamBRoundWins={roundResult.teamBRoundWins}
          onClose={handleRoundResultClose}
        />
      )}
      {gameResult && (() => {
        const winnerTeam: TeamId = gameResult.oppositionWon
          ? (gameResult.callerTeam === TeamId.A ? TeamId.B : TeamId.A)
          : gameResult.callerTeam;
        const iWon = myPlayerInfo.teamId === winnerTeam;
        return (
          <>
            <ConfettiCelebration trigger={!!gameResult} won={iWon} />
            <GameOverScreen
              result={gameResult}
              myTeam={myPlayerInfo.teamId}
              players={players}
              onPlayAgain={handlePlayAgain}
              matchTeamPoints={gameOverExtras?.matchTeamPoints}
              matchGamesPlayed={gameOverExtras?.matchGamesPlayed}
              matchPlayerPoints={gameOverExtras?.matchPlayerPoints}
              onLeaveRoom={() => socketConnection.leaveRoom()}
            />
          </>
        );
      })()}
      {redealMessage && (
        <Toast
          message={redealMessage}
          severity="info"
          onClose={() => setRedealMessage(null)}
        />
      )}
      {reactionAnnounce && (
        <Toast
          message={reactionAnnounce}
          severity="info"
          onClose={() => setReactionAnnounce(null)}
        />
      )}
      {showMyTurnToast && (
        <Toast
          key={myTurnToastKey}
          message="Your turn — please play a card."
          severity="info"
          onClose={() => setShowMyTurnToast(false)}
        />
      )}
      <ReactionOverlay reactions={reactionQueue} onComplete={handleReactionComplete} />
      <TableGiphyOverlay queue={giphyQueue} onDismissHead={dismissGiphyHead} />

      {reactionMenuTarget && (
        <ReactionMenu
          anchorX={reactionMenuTarget.anchorX}
          anchorY={reactionMenuTarget.anchorY}
          targetName={reactionMenuTarget.playerName}
          cooldownMs={reactionCooldownMs}
          onSelect={handleReactionSelect}
          onClose={closeReactionMenu}
        />
      )}

      {dealIntro && myPlayerInfo.teamId === dealIntro.trailingTeam && (
        <TrailingShuffleDealIntro
          key={String(dealIntro.deadlineMs ?? "intro")}
          tapsDone={dealIntro.tapsByUuid[myPlayerInfo.uuid] ?? 0}
          disabled={(dealIntro.tapsByUuid[myPlayerInfo.uuid] ?? 0) >= 3}
          onCommitTap={() => socketConnection.trailingShuffleDeckTap()}
          deadlineMs={dealIntro.deadlineMs}
        />
      )}
      {dealIntro && myPlayerInfo.teamId !== dealIntro.trailingTeam && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 1195,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(0,0,0,0.78)",
            px: 3,
            pointerEvents: "none",
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: { xs: "min(560px, 100%)", sm: 560 },
              textAlign: "center",
              p: 3,
              borderRadius: 2,
              border: "1px solid rgba(255,255,255,0.12)",
              bgcolor: "rgba(26,26,46,0.95)",
              pointerEvents: "auto",
              maxHeight: "min(88vh, 900px)",
              overflowY: "auto",
              WebkitOverflowScrolling: "touch",
            }}
          >
            <Typography sx={{ color: "#fff", fontWeight: 700, mb: 1.5, fontSize: "1.05rem" }}>
              Waiting on the trailing team
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.72)", fontSize: "0.88rem", lineHeight: 1.5 }}>
              Team {dealIntro.trailingTeam} is finishing the shuffle ritual (each player places three cards). Bidding
              starts when they finish. Use the reactions below (everyone sees them), or tap a trailing player avatar on
              the table to aim the animation at someone specific.
            </Typography>
            <DealIntroWaitingTauntPanel
              trailingTeam={dealIntro.trailingTeam}
              players={players}
              cooldownMs={reactionCooldownMs}
              onPickReaction={sendDealIntroTauntReaction}
            />
          </Box>
        </Box>
      )}

      {/* ── OUTER CONTAINER: full viewport width ── */}
      <Box
        sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: { sm: "100vh", lg: "auto" },
        boxSizing: "border-box",
        /* Edge-to-edge; only respect device safe areas (notch / home indicator). */
        pl: "env(safe-area-inset-left, 0px)",
        pr: "env(safe-area-inset-right, 0px)",
        pb: "env(safe-area-inset-bottom, 0px)",
        pt: 0,
        gap: { xs: 1, sm: 0.25, md: 1.5 },
        overflowX: { sm: "hidden", lg: "visible" },
        overflowY: { sm: "auto", lg: "visible" },
        WebkitOverflowScrolling: "touch",
      }}
      >

        {/* ── TOP BAR ──
            Equal-width columns so the turn strip stays visually centered; flex+space-between
            made the center flex grow unevenly when left/right chrome differed, which looked
            like the banner "jumped" when trump or turn text changed. */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)",
            alignItems: "center",
            width: "100%",
            gap: 1,
          }}
        >
          <Box
            sx={{
              justifySelf: "start",
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 0.35,
              width: { xs: "min(260px, 100%)", lg: "200px" },
            }}
          >
            {/* Trump indicator in the fixed left stack across all sizes */}
            <Box sx={{ display: "block", width: "100%" }}>
              {trumpSuit && trumpCallerTeam ? (
                <TrumpIndicator suit={trumpSuit} callerTeam={trumpCallerTeam} compact />
              ) : (
                <Box
                  sx={{
                    width: "100%",
                    minHeight: 48,
                    borderRadius: 2,
                    border: "1px dashed rgba(255,255,255,0.26)",
                    backgroundColor: "rgba(255,255,255,0.04)",
                    color: "rgba(255,255,255,0.62)",
                    fontSize: "0.78rem",
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  Trump pending
                </Box>
              )}
            </Box>
            {showStakeHud ? (
              <Box
                key={stakePulseTick}
                sx={{
                  width: "100%",
                  animation: "stakePulse 520ms ease-out",
                  "@keyframes stakePulse": {
                    "0%": { transform: "scale(0.98)", opacity: 0.7 },
                    "50%": { transform: "scale(1.02)", opacity: 1 },
                    "100%": { transform: "scale(1)", opacity: 1 },
                  },
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    borderRadius: 1.5,
                    px: 0.85,
                    py: 0.4,
                    background:
                      "linear-gradient(90deg, rgba(255,193,7,0.18) 0%, rgba(255,152,0,0.26) 50%, rgba(255,193,7,0.18) 100%)",
                    border: "1px solid rgba(255,193,7,0.45)",
                    boxShadow: "0 6px 18px rgba(255,152,0,0.22)",
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1.25 }}>
                    <Typography sx={{ color: "#ffe082", fontWeight: 700, fontSize: { xs: "0.68rem", sm: "0.76rem" } }}>
                      Current Stake
                    </Typography>
                    <Typography sx={{ color: "#fff8e1", fontWeight: 900, fontSize: { xs: "0.8rem", sm: "0.9rem" } }}>
                      {currentStakeLabel}
                    </Typography>
                  </Box>
                  {challengeBroadcast ? (
                    <Typography sx={{ mt: 0.4, color: "rgba(255,255,255,0.86)", fontSize: { xs: "0.68rem", sm: "0.74rem" } }}>
                      {challengeBroadcast}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
            ) : null}
          </Box>

          <Box
            sx={{
              justifySelf: "stretch",
              minWidth: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {turnLabel ? (
              <Box
                sx={{
                  width: "100%",
                  maxWidth: "100%",
                  textAlign: "center",
                  borderRadius: 1.2,
                  px: { xs: 1, sm: 2 },
                  py: { xs: 0.5, sm: 0.6 },
                  fontSize: { xs: "0.86rem", sm: "0.95rem" },
                  fontWeight: isMyActiveLabel ? "bold" : "normal",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  boxSizing: "border-box",
                  border: "1px solid transparent",
                  ...(isMyActiveLabel
                    ? {
                        background:
                          "linear-gradient(90deg, rgba(33,120,54,0.88) 0%, rgba(46,160,73,0.9) 50%, rgba(33,120,54,0.88) 100%)",
                        color: "#f7fff8",
                        borderColor: "rgba(117,214,143,0.5)",
                        boxShadow: "0 4px 14px rgba(35,135,61,0.32)",
                      }
                    : {
                        backgroundColor: "rgba(255,255,255,0.08)",
                        color: "rgba(255,255,255,0.86)",
                        borderColor: "rgba(255,255,255,0.14)",
                      }),
                }}
              >
                {turnLabel}
              </Box>
            ) : null}
          </Box>

          {/* Trump indicator — right side on desktop only */}
          <Box
            sx={{
              justifySelf: "end",
              minWidth: 0,
              display: { xs: "none", sm: "none", lg: "block" },
              justifyContent: "flex-end",
              alignItems: "center",
              minHeight: 1,
            }}
          >
            <Box sx={{ width: 90, flexShrink: 0 }} aria-hidden />
          </Box>
        </Box>

        {signedIn && phase === "PLAYING" && (
          <Box sx={{ display: "flex", justifyContent: "center", width: "100%", px: 0.5 }}>
            <Button
              size="small"
              color="warning"
              variant="outlined"
              onClick={() => {
                if (window.confirm("Forfeit the match and return to the lobby?")) {
                  socketConnection.forfeitMatch();
                }
              }}
            >
              Forfeit match
            </Button>
          </Box>
        )}

        {/* ── CONTENT AREA: 1-col xs, 2-col sm/tablet, 3-col desktop ── */}
        <Box
          sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            /* Laptop/tablet: give center table and hand column more room. */
            sm: "minmax(0, 1fr) minmax(240px, min(42vw, 360px))",
            lg: "200px 1fr 270px",
          },
          gap: { xs: 0, sm: 0.75, lg: 1.4 },
          /* sm/tablet "center" vertically collided the table with the turn strip on short viewports */
          alignItems: { xs: "start", sm: "start", lg: "start" },
          flex: { sm: 1 },
          width: "100%",
          minHeight: 0,
        }}
        >

          {/* ── LEFT SIDEBAR: Teams + scores (GIPHY last so table/hand stay primary on small landscape scroll) ── */}
          <Box
            sx={{
            display: { xs: "none", lg: "flex" },
            flexDirection: "column",
            gap: 1.2,
            backgroundColor: "rgba(0,0,0,0.45)",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 2.2,
            p: 1.2,
          }}
          >
            <TeamSidebar
              team={TeamId.A}
              players={teamAPlayers}
              myUUID={myPlayerInfo.uuid}
              teamColor="#4caf50"
              marks={teamAMarks}
              isCallerTeam={callerTeamId !== undefined ? callerTeamId === TeamId.A : undefined}
              attaCallerName={callerTeamId === TeamId.A ? callerName : null}
              callerSeatIndex={currentCallerIndex}
              getPlayerMatchNet={(uuid) => individualRows.find((r) => r.uuid === uuid)?.net ?? 0}
            />
            <Divider sx={{ borderColor: "rgba(255,255,255,0.1)" }} />
            <TeamSidebar
              team={TeamId.B}
              players={teamBPlayers}
              myUUID={myPlayerInfo.uuid}
              teamColor="#ffc107"
              marks={teamBMarks}
              isCallerTeam={callerTeamId !== undefined ? callerTeamId === TeamId.B : undefined}
              attaCallerName={callerTeamId === TeamId.B ? callerName : null}
              callerSeatIndex={currentCallerIndex}
              getPlayerMatchNet={(uuid) => individualRows.find((r) => r.uuid === uuid)?.net ?? 0}
            />
            <Divider sx={{ borderColor: "rgba(255,255,255,0.1)" }} />
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.8rem" }}>
                Card Points
              </Typography>
              <Switch
                checked={showCardPoints}
                onChange={(e) => setShowCardPoints(e.target.checked)}
                size="small"
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: "#66bb6a" },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#66bb6a" },
                }}
              />
            </Box>
            <Divider sx={{ borderColor: "rgba(255,255,255,0.1)" }} />
            <TableGiphyInlinePanel
              onPick={shareTableGiphyPick}
              pickDisabled={giphyCooldownMs > 0}
              gridWidth={168}
            />
          </Box>

          {/* ── CENTER COLUMN ── */}
          <Box
            sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: { xs: 1, sm: 0.25, md: 1 },
            minWidth: 0,
          }}
          >

            {/* Mobile score strip (portrait only) */}
            <Box
              sx={{
                display: { xs: "flex", sm: "none" },
                flexDirection: "column",
                gap: 0.75,
                width: "100%",
                p: 0.9,
                borderRadius: 1.6,
                backgroundColor: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(255,255,255,0.08)",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                <Typography sx={{ color: "rgba(255,255,255,0.72)", fontSize: "0.7rem", fontWeight: 600 }}>
                  Match{" "}
                  <Box component="span" sx={{ color: "#81c784", fontWeight: 700 }}>A {fmtSignedNet(matchHud.A)}</Box>
                  {" · "}
                  <Box component="span" sx={{ color: "#ffca28", fontWeight: 700 }}>B {fmtSignedNet(matchHud.B)}</Box>
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
                  <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.68rem", fontWeight: 600 }}>
                    Pts
                  </Typography>
                  <Switch
                    checked={showCardPoints}
                    onChange={(e) => setShowCardPoints(e.target.checked)}
                    size="small"
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": { color: "#66bb6a" },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#66bb6a" },
                    }}
                  />
                </Box>
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 0.6, alignItems: "stretch" }}>
                <Box sx={{ borderRadius: 1.2, px: 0.7, py: 0.55, border: "1px solid rgba(76,175,80,0.38)", backgroundColor: "rgba(76,175,80,0.08)" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, justifyContent: "space-between" }}>
                    <Box sx={{ color: "#66bb6a", fontWeight: 700, fontSize: "0.76rem", letterSpacing: 0.35 }}>TEAM A</Box>
                    {callerTeamId === TeamId.A && (
                      <Box sx={{ fontSize: "0.58rem", fontWeight: 700, color: "#66bb6a", backgroundColor: "rgba(102,187,106,0.2)", borderRadius: 0.8, px: 0.35, lineHeight: 1.35 }}>
                        ATT
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ color: "#7bd88f", fontWeight: 800, fontSize: "1.12rem", lineHeight: 1.1 }}>{teamAMarks}</Box>
                </Box>
                <Box sx={{ alignSelf: "center", color: "rgba(255,255,255,0.46)", fontSize: "0.72rem", fontWeight: 700 }}>VS</Box>
                <Box sx={{ borderRadius: 1.2, px: 0.7, py: 0.55, border: "1px solid rgba(255,193,7,0.38)", backgroundColor: "rgba(255,193,7,0.08)" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, justifyContent: "space-between" }}>
                    <Box sx={{ color: "#ffd54f", fontWeight: 700, fontSize: "0.76rem", letterSpacing: 0.35 }}>TEAM B</Box>
                    {callerTeamId === TeamId.B && (
                      <Box sx={{ fontSize: "0.58rem", fontWeight: 700, color: "#66bb6a", backgroundColor: "rgba(102,187,106,0.2)", borderRadius: 0.8, px: 0.35, lineHeight: 1.35 }}>
                        ATT
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ color: "#ffd54f", fontWeight: 800, fontSize: "1.12rem", lineHeight: 1.1, textAlign: "right" }}>{teamBMarks}</Box>
                </Box>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                <Typography sx={{ color: "rgba(255,255,255,0.58)", fontSize: "0.68rem" }}>
                  Deals {matchHud.gamesPlayed}
                </Typography>
                {currentCallerIndex !== null && callerName ? (
                  <Typography sx={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.7)" }}>
                    Atta: <Box component="span" sx={{ fontWeight: 700, color: "#81c784" }}>{callerName}</Box>
                  </Typography>
                ) : (
                  <Box />
                )}
              </Box>
            </Box>

            {/* Circular table — padding-bottom reserves space for avatars translated outside the felt
                (otherwise bidding / trump UI flows into the same pixels as the bottom seat). */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                width: "100%",
                position: "relative",
                zIndex: 0,
                pt: { xs: 1, sm: 2, md: 1.5 },
                pb: { xs: 10, sm: 9, md: 11 },
                "@media (orientation: landscape) and (min-width:900px)": {
                  pt: 4,
                  pb: 4,
                },
                backgroundColor: { lg: "rgba(0,0,0,0.5)" },
                border: { lg: "1px solid rgba(255,255,255,0.1)" },
                borderRadius: { lg: 2.2 },
                minHeight: { sm: 360, md: 400, lg: 420 },
                "@media (max-width:899px) and (max-height:520px)": {
                  justifyContent: "flex-start",
                  pl: "clamp(8px, 3vw, 24px)",
                  pt: 0.5,
                  pb: 4,
                },
              }}
            >
              <GameTableWithPlayers
                players={players}
                mySeatIndex={myPlayerInfo.seatIndex}
                dealerIndex={dealerIndex}
                currentTurnIndex={currentTurnIndex}
                currentRound={currentRound}
                trumpSuit={trumpSuit}
                onAvatarClick={handleAvatarClick}
                tableCenterOverlay={
                  deal1Phase === "shuffle" ? (
                    <HandShuffleOverlay placement="table" loop={false} />
                  ) : null
                }
              />
            </Box>

            {/* Bidding panel */}
            {phase === "BIDDING" && (
              <Box
                sx={{
                  width: "100%",
                  display: { xs: "none", sm: "flex" },
                  justifyContent: "center",
                  position: "relative",
                  zIndex: 12,
                }}
              >
                <BiddingPanel
                  isMyTurn={currentBidderIndex === myPlayerInfo.seatIndex}
                  currentCallerName={currentCallerIndex !== null ? callerName : null}
                  onBid={handleBid}
                  bidHistory={bidHistory}
                  currentBidderName={playerBySeat(currentBidderIndex)?.name ?? `Seat ${currentBidderIndex}`}
                  forcedBid={forcedBid}
                />
              </Box>
            )}

            {/* Trump pick panel */}
            {phase === "TRUMP_PICK" && !trumpSuit && (
              <Box
                sx={{
                  width: "100%",
                  display: { xs: "none", sm: "flex" },
                  justifyContent: "center",
                  position: "relative",
                  zIndex: 12,
                }}
              >
                <TrumpPickPanel
                  isCaller={currentCallerIndex === myPlayerInfo.seatIndex}
                  callerName={callerName}
                  stake={trumpStake}
                  onPickTrump={handlePickTrump}
                />
              </Box>
            )}

            {/* Player hand (xs + desktop) */}
            <Box
              sx={{
                display: { xs: "block", sm: "none", lg: "block" },
                width: "100%",
                backgroundColor: { lg: "rgba(0,0,0,0.5)" },
                border: { lg: "1px solid rgba(255,255,255,0.1)" },
                borderRadius: { lg: 2.2 },
                px: { lg: 1 },
                pt: { lg: 1 },
                pb: { lg: 0.6 },
              }}
            >
              <PlayerHand
                hand={hand}
                hideHandUntilDealShown={dealIntro !== null || (handDealVersion === 1 && deal1Phase !== "shown")}
                isMyTurn={
                  phase === "PLAYING" &&
                  currentTurnIndex === myPlayerInfo.seatIndex &&
                  !currentRound.some((pc) => pc.playerIndex === myPlayerInfo.seatIndex) &&
                  !roundResult
                }
                isPlayingPhase={phase === "PLAYING"}
                ledSuit={ledSuit}
                trumpSuit={trumpSuit}
                currentRound={currentRound}
                onPlayCard={handlePlayCard}
                isCallerTeam={
                  currentCallerIndex !== null
                    ? myPlayerInfo.teamId === playerBySeat(currentCallerIndex)?.teamId
                    : undefined
                }
                showPoints={showCardPoints}
                trumpRippleTick={trumpRippleTick}
                compactLandscape={isShortLandscapePhoneLike}
              />
            </Box>

            {/* Mobile-only tactical panels go BELOW hand so table + hand stay visible first */}
            <Box sx={{ display: { xs: "block", sm: "none" }, width: "100%" }}>
              {phase === "BIDDING" && (
                <Box sx={{ width: "100%", display: "flex", justifyContent: "center", position: "relative", zIndex: 12, mb: 1 }}>
                  <BiddingPanel
                    isMyTurn={currentBidderIndex === myPlayerInfo.seatIndex}
                    currentCallerName={currentCallerIndex !== null ? callerName : null}
                    onBid={handleBid}
                    bidHistory={bidHistory}
                    currentBidderName={playerBySeat(currentBidderIndex)?.name ?? `Seat ${currentBidderIndex}`}
                    forcedBid={forcedBid}
                  />
                </Box>
              )}
              {phase === "TRUMP_PICK" && !trumpSuit && (
                <Box sx={{ width: "100%", display: "flex", justifyContent: "center", position: "relative", zIndex: 12, mb: 1 }}>
                  <TrumpPickPanel
                    isCaller={currentCallerIndex === myPlayerInfo.seatIndex}
                    callerName={callerName}
                    stake={trumpStake}
                    onPickTrump={handlePickTrump}
                  />
                </Box>
              )}
              {showBidStrip && bidHistory.length > 0 && (
                <Box sx={{
                  backgroundColor: "rgba(0,0,0,0.5)",
                  borderRadius: 1.5,
                  p: 1,
                  mb: 1,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 0.5,
                  alignItems: "center",
                }}>
                  <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: "0.85rem", fontWeight: "bold", mr: 0.5 }}>
                    BIDS:
                  </Typography>
                  {bidHistory.map((entry, i) => (
                    <Chip
                      key={i}
                      label={`${entry.playerName}: ${entry.action}`}
                      size="small"
                      sx={{
                        height: 24,
                        fontSize: "0.8rem",
                        backgroundColor: entry.action === "Atta" ? "rgba(76,175,80,0.2)" : "rgba(255,255,255,0.08)",
                        color: entry.action === "Atta" ? "#4caf50" : "rgba(255,255,255,0.6)",
                      }}
                    />
                  ))}
                </Box>
              )}
              {phase === "PLAYING" && (
                <RoundArea
                  round={currentRound}
                  players={players}
                  currentTurnIndex={currentTurnIndex}
                  roundLeaderIndex={roundLeaderIndex}
                  totalPlayers={players.length}
                  trumpSuit={trumpSuit}
                  roundsPlayed={teamARoundWins + teamBRoundWins}
                  totalRounds={totalRounds}
                  callerIndex={currentCallerIndex}
                  teamARoundWins={teamARoundWins}
                  teamBRoundWins={teamBRoundWins}
                  layout="inline"
                  slamSeatIndex={slamSeatIndex}
                  slamMotionTick={slamMotionTick}
                  trumpMotionTick={trumpRippleTick}
                />
              )}
            </Box>

            {/* GIPHY: xs–md only, after table + hand + mobile HUD — scroll down; one short row */}
            <Box
              sx={{
                display: { xs: "block", lg: "none" },
                width: "100%",
                maxWidth: "100%",
                px: { xs: 0.5, sm: 0 },
                mt: { xs: 0.75, sm: 1 },
                pb: { xs: 1, sm: 1.25 },
              }}
            >
              <TableGiphyInlinePanel
                onPick={shareTableGiphyPick}
                pickDisabled={giphyCooldownMs > 0}
                compactStrip
              />
            </Box>
          </Box>

          {/* ── RIGHT SIDEBAR: scores (sm), hand (sm), bid/round info ── */}
          <Box
            sx={{
            display: { xs: "none", sm: "flex" },
            flexDirection: "column",
            gap: 1,
            pt: 0,
            minWidth: 0,
            backgroundColor: { lg: "rgba(0,0,0,0.45)" },
            border: { lg: "1px solid rgba(255,255,255,0.1)" },
            borderRadius: { lg: 2.2 },
            p: { lg: 1 },
            maxHeight: { sm: "min(calc(100dvh - 56px), calc(100vh - 56px))", lg: "none" },
            overflowY: { sm: "auto", lg: "visible" },
            WebkitOverflowScrolling: "touch",
          }}
          >
            {/* Player hand in sidebar for sm screens (keep at top) */}
            <Box sx={{ display: { sm: "block", lg: "none" } }}>
              <PlayerHand
                hand={hand}
                hideHandUntilDealShown={dealIntro !== null || (handDealVersion === 1 && deal1Phase !== "shown")}
                isMyTurn={
                  phase === "PLAYING" &&
                  currentTurnIndex === myPlayerInfo.seatIndex &&
                  !currentRound.some((pc) => pc.playerIndex === myPlayerInfo.seatIndex) &&
                  !roundResult
                }
                isPlayingPhase={phase === "PLAYING"}
                ledSuit={ledSuit}
                trumpSuit={trumpSuit}
                currentRound={currentRound}
                onPlayCard={handlePlayCard}
                isCallerTeam={
                  currentCallerIndex !== null
                    ? myPlayerInfo.teamId === playerBySeat(currentCallerIndex)?.teamId
                    : undefined
                }
                showPoints={showCardPoints}
                trumpRippleTick={trumpRippleTick}
                compactLandscape={isShortLandscapePhoneLike}
              />
            </Box>
            {/* Compact score strip for sm screens */}
            <Box sx={{
              display: { sm: "flex", lg: "none" },
              flexDirection: "column",
              gap: 0.35,
              backgroundColor: "rgba(0,0,0,0.5)",
              borderRadius: 1.5,
              px: 1,
              py: 0.5,
            }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 0.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, flexWrap: "wrap", minWidth: 0 }}>
                  <Box sx={{ color: "#4caf50", fontWeight: "bold", fontSize: "0.75rem" }}>A</Box>
                  {callerTeamId === TeamId.A && (
                    <Box sx={{ fontSize: "0.55rem", fontWeight: "bold", color: "#66bb6a", backgroundColor: "rgba(102,187,106,0.2)", borderRadius: 0.5, px: 0.3, lineHeight: 1.3, flexShrink: 0 }}>ATT</Box>
                  )}
                  <Box sx={{ color: "#4caf50", fontWeight: "bold", fontSize: "1rem", flexShrink: 0 }}>{teamAMarks}</Box>
                </Box>
                <Box sx={{ color: "rgba(255,255,255,0.4)", fontSize: "0.65rem", flexShrink: 0 }}>vs</Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, flexWrap: "wrap", justifyContent: "flex-end", minWidth: 0 }}>
                  <Box sx={{ color: "#ffc107", fontWeight: "bold", fontSize: "1rem", flexShrink: 0 }}>{teamBMarks}</Box>
                  {callerTeamId === TeamId.B && (
                    <Box sx={{ fontSize: "0.55rem", fontWeight: "bold", color: "#66bb6a", backgroundColor: "rgba(102,187,106,0.2)", borderRadius: 0.5, px: 0.3, lineHeight: 1.3, flexShrink: 0 }}>ATT</Box>
                  )}
                  <Box sx={{ color: "#ffc107", fontWeight: "bold", fontSize: "0.75rem" }}>B</Box>
                </Box>
              </Box>
              {currentCallerIndex !== null && callerName && (
                <Typography sx={{ fontSize: "0.65rem", color: "rgba(255,255,255,0.65)", textAlign: "center", width: "100%" }}>
                  Atta: <Box component="span" sx={{ fontWeight: 700, color: "#81c784" }}>{callerName}</Box>
                </Typography>
              )}
              {/* Keep compact strip minimal on small landscape */}
            </Box>
            {showBidStrip && bidHistory.length > 0 && (
              <Box sx={{
                backgroundColor: "rgba(0,0,0,0.52)",
                border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: 2.2,
                p: 0,
                color: "white",
                width: "100%",
              }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 1.5, py: 1.1, borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
                  <Typography sx={{ color: "rgba(255,255,255,0.84)", letterSpacing: 1.4, fontSize: "0.82rem", fontWeight: 800 }}>
                    PLAY HISTORY
                  </Typography>
                  <Box sx={{
                    minWidth: 24,
                    height: 24,
                    borderRadius: 12,
                    px: 0.8,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.78rem",
                    fontWeight: 700,
                    color: "rgba(255,255,255,0.84)",
                    backgroundColor: "rgba(255,255,255,0.11)",
                  }}>
                    {bidHistory.length}
                  </Box>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.7, p: 1.2 }}>
                  {bidHistory.map((entry, i) => (
                    <Box
                      key={i}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1.2,
                        px: 1.1,
                        py: 0.95,
                        borderRadius: 1.3,
                        backgroundColor: "rgba(255,255,255,0.05)",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, overflow: "hidden" }}>
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            flexShrink: 0,
                            backgroundColor: entry.action === "Atta" ? "#56d46f" : "#ffa32f",
                          }}
                        />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            sx={{
                              color: "#e8eef6",
                              fontSize: "0.98rem",
                              fontWeight: 700,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              lineHeight: 1.2,
                            }}
                          >
                            {entry.playerName}
                          </Typography>
                          <Typography sx={{ color: "rgba(207,221,237,0.72)", fontSize: "0.8rem", lineHeight: 1.2 }}>
                            Bid: {entry.action}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
                {currentCallerIndex !== null && (
                  <Typography sx={{ px: 1.4, pb: 1.1, color: "rgba(255,255,255,0.7)", fontSize: "0.78rem" }}>
                    Caller: <Box component="span" sx={{ color: "#f8d15b", fontWeight: 700 }}>{callerName}</Box>
                  </Typography>
                )}
              </Box>
            )}
            {phase === "PLAYING" && (
              <Box>
                <Box
                  onClick={() => setRoundExpanded((p) => !p)}
                  sx={{
                    display: { xs: "none", sm: "flex", md: "none" },
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                    backgroundColor: "rgba(0,0,0,0.5)",
                    borderRadius: 1,
                    px: 1,
                    py: 0.5,
                    mb: roundExpanded ? 0.5 : 0,
                    userSelect: "none",
                  }}
                >
                  <Typography sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.75rem", fontWeight: "bold" }}>
                    ROUND
                  </Typography>
                  <Typography sx={{ color: "rgba(255,255,255,0.4)", fontSize: "0.75rem" }}>
                    {roundExpanded ? "▲" : "▼"}
                  </Typography>
                </Box>
                <Box sx={{ display: { sm: roundExpanded ? "block" : "none", md: "block" } }}>
                  <RoundArea
                    round={currentRound}
                    players={players}
                    currentTurnIndex={currentTurnIndex}
                    roundLeaderIndex={roundLeaderIndex}
                    totalPlayers={players.length}
                    trumpSuit={trumpSuit}
                    roundsPlayed={teamARoundWins + teamBRoundWins}
                    totalRounds={totalRounds}
                    callerIndex={currentCallerIndex}
                    teamARoundWins={teamARoundWins}
                    teamBRoundWins={teamBRoundWins}
                    layout="sidebar"
                    slamSeatIndex={slamSeatIndex}
                    slamMotionTick={slamMotionTick}
                    trumpMotionTick={trumpRippleTick}
                  />
                </Box>
              </Box>
            )}
          </Box>

        </Box>
      </Box>
    </>
  );
};
